'use strict';
const angular = require('angular');
const {copy, forEach, isArray, isFunction, isObject, isUndefined} = angular;

export function ObservableArrayFactoryProvider() {
  var EVENT_TYPES = {};

  Object.defineProperties(EVENT_TYPES, {
    element_added: {value: 'element_added'},
    element_updated: {value: 'element_updated'},
    element_removed: {value: 'element_removed'}
  });

  return {
    $get: ['$log',
      function ($log,
      ) {
        // currently reverse and sort are not supported.
        // slice is strongly discouraged.

        var eventsSupported = ['element_added', 'element_updated', 'element_removed'];
        var delegatedMethods = ['concat', 'every', 'filter', 'forEach', 'indexOf', 'join', 'lastIndexOf',
          'map', 'reduce', 'reduceRight', 'slice', 'some', 'toLocaleString', 'toString'];

        /**
         * @constructor
         */
        function ObservableArray(initialArray, initialListeners) {
          Object.defineProperty(this, 'className', {
            value: 'ObservableArray'
          });
          Object.defineProperty(this, '$$listeners', {
            value: {element_added: [], element_updated: [], element_removed: []}
          });
          Object.defineProperty(this, '$$internalArray', {
            value: []
          });
          Object.defineProperty(this, 'length', {
            get: function () {
              return this.$$internalArray.length;
            }
          });

          if (isArray(initialArray)) {
            forEach(initialArray, function (value) {
              this.push(value);
            }, this);
          }

          if (isArray(initialListeners)) {
            forEach(initialListeners, function (listener) {
              isObject(listener) && this.addListener(listener.event, listener.callback, listener.context);
            }, this);
          }

          // Prevent additional (illegal) property assignment
          Object.preventExtensions(this);
        }

        ObservableArray.prototype = {
          empty: function () {
            while (this.$$internalArray.length > 0) {
              this.pop();
            }
          },

          addListener: function (event, callback, context) {
            if (!isValidEvent(event)) throw new Error('ObservableArray: Invalid event ' + event);
            if (!isFunction(callback)) throw new Error('Callback is not a function!');
            if (isUndefined(context)) context = null;
            _fireOnSubEvents.call(this, event, callback, context);
            this.$$listeners[event].push({callback: callback, context: context});
            return callback;
          },

          /**
           * @param {String|Array} [event]
           * @returns {boolean}
           */
          hasListeners: function (event) {
            if (isUndefined(event)) {
              return (this.getListeners().length);
            } else if (isValidEvent(event)) {
              return this.$$listeners[event].length > 0;
            } else {
              $log.warn('ObservableArray.hasListeners invalid event type %s', event);
            }
          },

          getListeners: function (event) {
            var list = [];
            if (isUndefined(event)) {
              forEach(eventsSupported, function (event) {
                if (this.$$listeners[event].length) {
                  list = list.concat(copy(this.$$listeners[event]));
                }
              }, this);
            } else {
              return copy(this.$$listeners[event]);
            }
            return list;
          },

          // Observed Methods:
          get: function (index) {
            return this.$$internalArray[index];
          },

          set: function (index, value) {
            var oldLength = this.$$internalArray.length;
            var oldValue = this.$$internalArray[index];
            this.$$internalArray[index] = value;
            if (oldLength === index) {
              this.$triggerEvent('element_added', value, index);
            } else if (oldValue !== value) {
              this.$triggerEvent('element_updated', value, oldValue, index);
            }
          },

          equals: function (other) {
            if (isUndefined(other) || other === null || !isObject(other) || !isFunction(other.get) || isUndefined(other.length)) return false;
            try {
              if (this.length !== other.length) return false;
              for (var i = 0; i < this.length; i++) {
                if (this.get(i) !== other.get(i)) {
                  return false;
                }
              }
            } catch (error) {
              $log.warn(error);
              return false;
            }
            return true;
          },

          push: function () {
            var originalLength = this.$$internalArray.length;
            var newLength = Array.prototype.push.apply(this.$$internalArray, arguments);
            for (var i = originalLength; i < newLength; i++) {
              this.$triggerEvent('element_added', this.$$internalArray[i], i);
            }
            return newLength;
          },

          pop: function () {
            var popped = Array.prototype.pop.apply(this.$$internalArray, arguments);
            this.$triggerEvent('element_removed', popped, this.$$internalArray.length);
            return popped;
          },

          shift: function () {
            var shifted = Array.prototype.shift.apply(this.$$internalArray, arguments);
            this.$triggerEvent('element_removed', shifted, 0);
            return shifted;
          },

          unshift: function () {
            var originalLength = this.$$internalArray.length;
            var newLength = Array.prototype.unshift.apply(this.$$internalArray, arguments);
            for (var i = 0; i < newLength - originalLength; i++) {
              this.$triggerEvent('element_added', this.$$internalArray[i], i);
            }
            return newLength;
          },

          splice: function (start, deleteCount) {
            var i;
            var deleted = Array.prototype.splice.apply(this.$$internalArray, arguments);

            for (i = 0; i < deleted.length; i++) {
              this.$triggerEvent('element_removed', deleted[i], start + i);
            }

            for (i = 2; i < arguments.length; i++) {
              this.$triggerEvent('element_added', arguments[i], start + i - 2);
            }

            return deleted;
          },

          $triggerEvent: function (event, arg1, arg2, arg3) {
            var i;
            switch (event) {
              case 'element_added':
              case 'element_removed':
                for (i = 0; i < this.$$listeners[event].length; i++) {
                  this.$$listeners[event][i].callback.call(this.$$listeners[event][i].context, arg1, arg2);
                }
                break;
              case 'element_updated':
                for (i = 0; i < this.$$listeners[event].length; i++) {
                  this.$$listeners[event][i].callback.call(this.$$listeners[event][i].context, arg1, arg2, arg3);
                }
                break;
            }
          },

          removeListener: function (event, reference) {
            if (!isValidEvent(event)) throw new Error('Invalid event ' + event);
            if (isUndefined(reference)) {
              while (this.$$listeners[event].length > 0) {
                this.$$listeners[event].pop();
              }
            } else {
              for (var i = 0; i < this.$$listeners[event].length; i++) {
                if (this.$$listeners[event][i].callback === reference) {
                  this.$$listeners[event].splice(i, 1);
                  i--;
                }
              }
            }
          },

          removeListeners: function (event, reference) {
            if (isUndefined(event)) {
              forEach(eventsSupported, function (event) {
                this.removeListener(event, reference);
              }, this);
            } else {
              this.removeListener(event, reference);
            }
          },

          destroy: function () {
            try {
              this.removeListeners();
            } catch (errors) {
              $log.warn(errors);
            }
          }
        };


        function _fireOnSubEvents(event, callback, context) {
          if (event === 'element_added') {
            for (var i = 0, length = this.$$internalArray; i < length; i++) {
              callback.call(context, this.$$internalArray[i], i);
            }
          }
        }

        // Generate delegate prototype methods:
        forEach(delegatedMethods, function (method) {
          ObservableArray.prototype[method] = function () {
            return Array.prototype[method].apply(this.$$internalArray, arguments);
          }
        });

        function isValidEvent(event) {
          return !!EVENT_TYPES[event];
        }

        return ObservableArray;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/_libs/angular-fire-util/ObservableArrayFactory.js